// Endorsement detail — full editable record + recalc panel + closings.
// Structurally mirrors ClaimDetail. Deal-scoped in intent (endorsements
// always attach to a deal) but reachable by direct URL for edit access.

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useHistory } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import useConfirm from '../../components/ConfirmDialog/useConfirm';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const C = {
  ink:      '#1e293b',
  muted:    '#64748b',
  border:   '#e2e8f0',
  soft:     '#f8fafc',
  burgundy: '#800020',
};

const S = {
  page:    { flex: 1, overflowY: 'auto', padding: '24px 40px', minHeight: 0 },
  crumbs:  { fontSize: 12, color: C.muted, marginBottom: 6 },
  crumbLink:{ color: C.muted, textDecoration: 'none', cursor: 'pointer' },
  head:    { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 4 },
  h1:      { fontSize: 22, fontWeight: 700, color: C.ink },
  sub:     { color: C.muted, fontSize: 13, margin: '4px 0 20px' },
  meta:    { fontSize: 11, color: C.muted },
  btn:     { padding: '9px 16px', borderRadius: 10, border: 'none', background: C.burgundy, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  btnDis:  { padding: '9px 16px', borderRadius: 10, border: 'none', background: '#cbd5e1', color: '#fff', cursor: 'not-allowed', fontWeight: 600, fontSize: 13 },
  ghost:   { padding: '9px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  danger:  { padding: '9px 16px', borderRadius: 10, border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  card:    { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, marginBottom: 16 },
  section: { fontSize: 12, fontWeight: 700, color: C.burgundy, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 14 },
  grid:    { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 },
  field:   { display: 'flex', flexDirection: 'column', gap: 4 },
  label:   { fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6 },
  input:   { padding: '9px 11px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: '#fff', color: C.ink, fontFamily: 'inherit' },
  ta:      { padding: '9px 11px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: '#fff', color: C.ink, minHeight: 78, resize: 'vertical', fontFamily: 'inherit' },
  pill:    (status) => ({
    display: 'inline-block', padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
    background: status === 'signed' ? '#dcfce7' : status === 'cancelled' ? '#fee2e2' : status === 'agreed' ? '#e0f2fe' : '#fef3c7',
    color:      status === 'signed' ? '#166534' : status === 'cancelled' ? '#991b1b' : status === 'agreed' ? '#075985' : '#92400e',
  }),
  deltaChip: (v) => ({
    display: 'inline-block', padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
    background: v > 0 ? '#ecfdf5' : v < 0 ? '#fef2f2' : '#f1f5f9',
    color:      v > 0 ? '#065f46' : v < 0 ? '#991b1b' : '#475569',
  }),
  table:   { width: '100%', borderCollapse: 'collapse', marginTop: 6, fontSize: 12 },
  th:      { textAlign: 'left', padding: '8px 10px', background: C.soft, color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` },
  td:      { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
  err:  { color: '#dc2626', fontSize: 13, marginTop: 8 },
  ok:   { color: '#166534', fontSize: 13, marginTop: 8 },
};

const TYPE_OPTIONS = ['premium_adjustment', 'sum_insured_change', 'period_extension', 'cancellation', 'other'];
const STATUS_OPTIONS = ['draft', 'agreed', 'signed', 'cancelled'];
const NUMERIC = ['premiumDelta', 'newSumInsured'];

const toDateInputValue = (v) => { if (!v) return ''; try { return new Date(v).toISOString().slice(0, 10); } catch (e) { return String(v).slice(0, 10); } };
const fmtMoney = (n, ccy) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  try { return `${ccy || ''} ${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim(); }
  catch (e) { return String(n); }
};
const fmtSignedMoney = (n, ccy) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const abs = Math.abs(Number(n));
  const sign = Number(n) < 0 ? '-' : '+';
  try { return `${sign}${ccy || ''} ${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
  catch (e) { return String(n); }
};
const fmtPct = (n) => (n == null || n === '' ? '—' : `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 3 })}%`);
const fmtDateTime = (v) => { if (!v) return '—'; try { const d = new Date(v); return `${d.toISOString().slice(0,10)} ${d.toISOString().slice(11,16)} UTC`; } catch (e) { return String(v); } };

function hydrateForm(e) {
  return {
    endorsementReference: e.endorsementReference || '',
    type:                 e.type || 'other',
    effectiveDate:        e.effectiveDate || '',
    description:          e.description || '',
    premiumDelta:         e.premiumDelta ?? '',
    currency:             e.currency || '',
    newSumInsured:        e.newSumInsured ?? '',
    newPeriodEnd:         e.newPeriodEnd || '',
    cancellationDate:     e.cancellationDate || '',
    status:               e.status || 'draft',
    notes:                e.notes || '',
  };
}
function buildPayload(form) {
  const out = { ...form };
  NUMERIC.forEach((k) => {
    if (out[k] === '' || out[k] == null) delete out[k];
    else out[k] = Number(out[k]);
  });
  return out;
}

const EndorsementDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const { confirm, ConfirmPortal } = useConfirm();

  const [endorsement, setE]   = useState(null);
  const [form, setForm]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saveOk, setSaveOk]   = useState(false);
  const [error, setError]     = useState(null);
  const [recalcBusy, setRecalcBusy]   = useState(false);
  const [recalcError, setRecalcError] = useState(null);
  const [closingBusy, setClosingBusy] = useState(null);
  const [closingError, setClosingError] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const resp = await axios.get(`${BASE_URL}/reinsurance/endorsements/${id}`, { withCredentials: true });
      const e = resp?.data?.data;
      if (!e) { setError('Endorsement not found'); return; }
      setE(e);
      setForm(hydrateForm(e));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load endorsement');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);

  const onField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setSaving(true); setError(null); setSaveOk(false);
    try {
      const resp = await axios.patch(`${BASE_URL}/reinsurance/endorsements/${id}`, buildPayload(form), { withCredentials: true });
      const e = resp?.data?.data;
      if (e) { setE(e); setForm(hydrateForm(e)); setSaveOk(true); setTimeout(() => setSaveOk(false), 2000); }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const recalc = async () => {
    setRecalcBusy(true); setRecalcError(null);
    try {
      const resp = await axios.post(`${BASE_URL}/reinsurance/endorsements/${id}/recalc`, {}, { withCredentials: true });
      const snap = resp?.data?.data;
      if (snap) setE((prev) => ({ ...prev, impact: snap }));
    } catch (err) {
      setRecalcError(err?.response?.data?.message || err.message || 'Recalc failed');
    } finally { setRecalcBusy(false); }
  };

  const deleteE = async () => {
    const ok = await confirm({
      title: 'Delete endorsement?',
      message: `${endorsement?.endorsementNumber || 'This endorsement'} will be permanently removed.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await axios.delete(`${BASE_URL}/reinsurance/endorsements/${id}`, { withCredentials: true });
      if (endorsement?.deal) history.push(`/reinsurance/deals/${endorsement.deal}`);
      else history.push('/reinsurance/deals');
    } catch (err) { setError(err?.response?.data?.message || err.message || 'Delete failed'); }
  };

  const generateClosing = async (kind, extras = {}) => {
    const busyKey = kind === 'reinsurer' ? `r:${extras.reinsurerIndex}` : 'cedant';
    setClosingBusy(busyKey); setClosingError(null);
    try {
      const resp = await axios.post(
        `${BASE_URL}/reinsurance/endorsements/${id}/closings/${kind}`,
        kind === 'reinsurer' ? { reinsurerIndex: extras.reinsurerIndex } : {},
        { withCredentials: true },
      );
      const row = resp?.data?.data;
      if (row) setE((prev) => ({ ...prev, closings: [...(prev.closings || []), row] }));
    } catch (err) {
      setClosingError(err?.response?.data?.message || err.message || 'Generation failed');
    } finally { setClosingBusy(null); }
  };

  const downloadClosing = async (closingId) => {
    try {
      const resp = await axios.get(`${BASE_URL}/reinsurance/endorsements/${id}/closings/${closingId}/download`, { withCredentials: true });
      const url = resp?.data?.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) { setClosingError(err?.response?.data?.message || err.message || 'Download link failed'); }
  };

  if (loading) return <AppLayout><div style={S.page}><div style={{ color: C.muted }}>Loading endorsement…</div></div></AppLayout>;
  if (error && !endorsement) return (
    <AppLayout>
      <div style={S.page}>
        <div style={S.crumbs}>
          <span style={S.crumbLink} onClick={() => history.push('/reinsurance/deals')}>Reinsurance Deals</span>
          <span> / endorsement / {id}</span>
        </div>
        <div style={S.err}>{error}</div>
      </div>
    </AppLayout>
  );

  const im = endorsement.impact;
  const hasImpact = !!im?.computedAt;
  const delta = Number(im?.dealTotals?.premiumDelta || 0);
  const isAdditional = delta > 0;

  return (
    <AppLayout>
      <div style={S.page}>
        <div style={S.crumbs}>
          <span style={S.crumbLink} onClick={() => history.push('/reinsurance/deals')}>Reinsurance Deals</span>
          {endorsement.deal && (
            <>
              <span> / </span>
              <span style={S.crumbLink} onClick={() => history.push(`/reinsurance/deals/${endorsement.deal}`)}>{endorsement.dealNumber || String(endorsement.deal).slice(-8)}</span>
            </>
          )}
          <span> / {endorsement.endorsementNumber || id}</span>
        </div>
        <div style={S.head}>
          <div>
            <div style={S.h1}>
              {endorsement.endorsementNumber || '(no number)'} <span style={{ marginLeft: 10 }}><span style={S.pill(endorsement.status)}>{endorsement.status}</span></span>
            </div>
            <div style={S.meta}>
              Deal <b style={{ color: C.ink, cursor: 'pointer' }} onClick={() => history.push(`/reinsurance/deals/${endorsement.deal}`)}>{endorsement.dealNumber || String(endorsement.deal).slice(-8)}</b>
              <> · {(endorsement.type || '').replace(/_/g, ' ')}</>
              {endorsement.effectiveDate && <> · effective {toDateInputValue(endorsement.effectiveDate)}</>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={S.ghost} onClick={() => history.push(`/reinsurance/deals/${endorsement.deal}`)}>← Back to deal</button>
            <button style={S.danger} onClick={deleteE}>Delete</button>
            <button style={saving ? S.btnDis : S.btn} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
          </div>
        </div>
        <p style={S.sub}>Mid-term change against the linked deal. Recalc allocates the premium delta across signed markets; closings render per-market and cedant PDFs from the snapshot.</p>

        {/* Endorsement fields */}
        <div style={S.card}>
          <div style={S.section}>Endorsement</div>
          <div style={S.grid}>
            <div style={S.field}>
              <label style={S.label}>Type *</label>
              <select style={S.input} value={form.type} onChange={onField('type')}>
                {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>Status</label>
              <select style={S.input} value={form.status} onChange={onField('status')}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>Effective Date *</label>
              <input type='date' style={S.input} value={toDateInputValue(form.effectiveDate)} onChange={onField('effectiveDate')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>External / Cedant Reference</label>
              <input style={S.input} value={form.endorsementReference} onChange={onField('endorsementReference')} />
            </div>
            <div style={{ ...S.field, gridColumn: '1 / -1' }}>
              <label style={S.label}>Description</label>
              <textarea style={S.ta} value={form.description} onChange={onField('description')} />
            </div>
          </div>
        </div>

        {/* Financial impact inputs */}
        <div style={S.card}>
          <div style={S.section}>Financial Impact</div>
          <div style={S.grid}>
            <div style={S.field}>
              <label style={S.label}>Premium Delta (signed)</label>
              <input
                type='number' style={S.input}
                placeholder='+ for additional, - for return'
                value={form.premiumDelta ?? ''} onChange={onField('premiumDelta')}
              />
            </div>
            <div style={S.field}>
              <label style={S.label}>Currency</label>
              <input style={S.input} value={form.currency} onChange={onField('currency')} placeholder='defaults to deal currency' />
            </div>
            {form.type === 'sum_insured_change' && (
              <div style={S.field}>
                <label style={S.label}>New Sum Insured</label>
                <input type='number' style={S.input} value={form.newSumInsured ?? ''} onChange={onField('newSumInsured')} />
              </div>
            )}
            {form.type === 'period_extension' && (
              <div style={S.field}>
                <label style={S.label}>New Period End</label>
                <input type='date' style={S.input} value={toDateInputValue(form.newPeriodEnd)} onChange={onField('newPeriodEnd')} />
              </div>
            )}
            {form.type === 'cancellation' && (
              <div style={S.field}>
                <label style={S.label}>Cancellation Date</label>
                <input type='date' style={S.input} value={toDateInputValue(form.cancellationDate)} onChange={onField('cancellationDate')} />
              </div>
            )}
          </div>
        </div>

        {/* Recalc panel */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <div style={S.section}>Recalc</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {hasImpact && <span style={S.meta}>Last run: {fmtDateTime(im.computedAt)}</span>}
              <button style={recalcBusy ? S.btnDis : S.btn} onClick={recalc} disabled={recalcBusy}>
                {recalcBusy ? 'Recalculating…' : (hasImpact ? 'Recalculate' : 'Recalculate impact')}
              </button>
            </div>
          </div>
          {recalcError && <div style={S.err}>{recalcError}</div>}
          {!hasImpact && !recalcError && (
            <div style={{ padding: 16, textAlign: 'center', color: C.muted, background: C.soft, borderRadius: 8, fontSize: 13 }}>
              Not calculated yet. Click <b>Recalculate impact</b> to allocate the premium delta across signed reinsurer markets on the linked deal.
            </div>
          )}
          {hasImpact && (
            <>
              {im.warnings && im.warnings.length > 0 && (
                <div style={{ marginBottom: 12, padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, color: '#92400e', fontSize: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Warnings ({im.warnings.length})</div>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {im.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              <div style={{ marginBottom: 6, fontSize: 13, color: C.ink }}>
                <b>Direction:</b> <span style={S.deltaChip(delta)}>{isAdditional ? 'ADDITIONAL premium (cedant pays reinsurers)' : delta < 0 ? 'RETURN premium (reinsurers refund cedant)' : 'zero delta'}</span>
              </div>

              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Deal Totals (signed)</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                  {[
                    ['Premium delta',           im.dealTotals?.premiumDelta],
                    ['Ceded portion',           im.dealTotals?.cededDelta],
                    ['Ceding commission delta', im.dealTotals?.cedingCommissionDelta],
                    ['Brokerage delta',         im.dealTotals?.brokerageDelta],
                    ['Net delta to reinsurers', im.dealTotals?.netDeltaToReinsurers],
                    ['Unallocated',             im.dealTotals?.unallocatedDelta],
                  ].map(([label, val], i) => (
                    <div key={i} style={{ padding: '10px 12px', background: C.soft, borderRadius: 8, border: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{fmtSignedMoney(val, im.dealTotals?.currency)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {im.allocations && im.allocations.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Per-Market Delta</div>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Reinsurer</th>
                        <th style={S.th}>Share</th>
                        <th style={S.th}>Premium Δ</th>
                        <th style={S.th}>Ceding Δ</th>
                        <th style={S.th}>Brokerage Δ</th>
                        <th style={S.th}>Net Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {im.allocations.map((a) => (
                        <tr key={a.reinsurerIndex}>
                          <td style={{ ...S.td, fontWeight: 600, color: C.ink }}>
                            {a.reinsurerName}
                            {a.currency && a.currency !== im.dealTotals?.currency && (
                              <div style={{ fontSize: 10, color: C.muted }}>settles in {a.currency} (FX at closing)</div>
                            )}
                          </td>
                          <td style={S.td}>{fmtPct(a.signedShare)}</td>
                          <td style={S.td}>{fmtSignedMoney(a.premiumDelta, im.dealTotals?.currency)}</td>
                          <td style={S.td}>{fmtSignedMoney(a.cedingCommissionDelta, im.dealTotals?.currency)}</td>
                          <td style={S.td}>{fmtSignedMoney(a.brokerageDelta, im.dealTotals?.currency)}</td>
                          <td style={{ ...S.td, fontWeight: 600 }}>{fmtSignedMoney(a.netDelta, im.dealTotals?.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Closings */}
        <div style={S.card}>
          <div style={S.section}>Endorsement Closings</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
            Tenant-branded PDFs from the impact snapshot. Direction (additional / return) determines the wording automatically.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <button
              style={closingBusy === 'cedant' || !hasImpact || delta === 0 ? S.btnDis : S.btn}
              disabled={!!closingBusy || !hasImpact || delta === 0}
              onClick={() => generateClosing('cedant')}
              title={!hasImpact ? 'Recalculate the endorsement first' : (delta === 0 ? 'No delta to close' : (isAdditional ? 'Debit note to cedant' : 'Credit note to cedant'))}
            >
              {closingBusy === 'cedant' ? 'Generating…' : (isAdditional ? 'Cedant debit (additional)' : 'Cedant credit (return)')}
            </button>
          </div>
          {hasImpact && im.allocations && im.allocations.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
                Per-market closings
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {im.allocations.map((a) => {
                  const busy = closingBusy === `r:${a.reinsurerIndex}`;
                  const dir = (a.premiumDelta || 0) > 0 ? 'credit' : 'debit';
                  return (
                    <button
                      key={a.reinsurerIndex}
                      style={busy ? S.btnDis : S.ghost}
                      disabled={!!closingBusy}
                      onClick={() => generateClosing('reinsurer', { reinsurerIndex: a.reinsurerIndex })}
                    >
                      {busy ? 'Generating…' : `${dir === 'credit' ? 'Credit' : 'Debit'} → ${a.reinsurerName}`}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {closingError && <div style={S.err}>{closingError}</div>}

          {endorsement.closings && endorsement.closings.length > 0 ? (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, margin: '14px 0 6px' }}>Generated</div>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Ref</th>
                    <th style={S.th}>Kind</th>
                    <th style={S.th}>Reinsurer</th>
                    <th style={S.th}>Filename</th>
                    <th style={S.th}>Generated</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {[...endorsement.closings].reverse().map((c) => (
                    <tr key={c._id}>
                      <td style={{ ...S.td, fontWeight: 600, color: C.burgundy }}>{c.ref || '—'}</td>
                      <td style={S.td}>{(c.kind || '').replace(/_/g, ' ')}</td>
                      <td style={S.td}>{c.reinsurerName || '—'}</td>
                      <td style={S.td}>{c.filename || '—'}</td>
                      <td style={S.td}>{fmtDateTime(c.generatedAt)}</td>
                      <td style={S.td}>
                        <button style={{ ...S.ghost, padding: '4px 10px', fontSize: 12 }} onClick={() => downloadClosing(c._id)}>Download</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div style={{ padding: 14, textAlign: 'center', color: C.muted, background: C.soft, borderRadius: 8, fontSize: 12 }}>No closings generated yet.</div>
          )}
        </div>

        {/* Notes */}
        <div style={S.card}>
          <div style={S.section}>Notes</div>
          <textarea style={S.ta} value={form.notes} onChange={onField('notes')} />
        </div>

        {error && <div style={S.err}>{error}</div>}
        {saveOk && <div style={S.ok}>Saved.</div>}
      </div>
      {ConfirmPortal}
    </AppLayout>
  );
};

export default EndorsementDetail;
